import cn from 'classnames';
import { type MemoExoticComponent, memo, type PropsWithChildren } from 'react';

import {
  ACHIEVEMENT_FALLBACK_TYPES,
  ACHIEVEMENT_TIERS,
  ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';

import { Tooltip } from '../../general/tooltip/Tooltip';
import { STICKER_SIZES } from '../sticker/constants';
import { Sticker } from '../sticker/Sticker';
import * as styles from './Achievement.styles';
import {
  ACHIEVEMENT_BACKGROUND_SIZE_MAP,
  TIER_BACKGROUND_MAP,
  TIER_PLACEHOLDER_MAP,
  TYPE_ICON_MAPPING,
} from './constants';

export type AchievementProps = {
  title?: string;
  tier?: ACHIEVEMENT_TIERS;
  type?: ACHIEVEMENT_TYPES | ACHIEVEMENT_FALLBACK_TYPES;
  size?: STICKER_SIZES;
  isDisabled?: boolean;
  isSeen?: boolean;
};

export type AchievementBackgroundProps = {
  size: number;
};

const AchievementBase = ({
  title,
  tier = ACHIEVEMENT_TIERS.NEUTRAL,
  type = ACHIEVEMENT_FALLBACK_TYPES.DEFAULT,
  size = STICKER_SIZES.MEDIUM,
  isDisabled = false,
  isSeen = true,
  children = null,
}: PropsWithChildren<AchievementProps>) => {
  if (isDisabled)
    return (
      <div css={styles.mainContainer}>
        {TIER_PLACEHOLDER_MAP(ACHIEVEMENT_BACKGROUND_SIZE_MAP[size])[tier]}
        {children && <div css={styles.childrenContainer}>{children}</div>}
      </div>
    );

  return (
    <div>
      <Tooltip content={title} disabled={!title}>
        <div css={styles.mainContainer} className={cn(size, { isSeen })}>
          {TIER_BACKGROUND_MAP(ACHIEVEMENT_BACKGROUND_SIZE_MAP[size])[tier]}

          <div
            css={styles.iconContainer}
            className={cn({ isNeutral: tier === ACHIEVEMENT_TIERS.NEUTRAL })}
          >
            <Sticker type={TYPE_ICON_MAPPING[type]} size={size} />
          </div>

          {children && <div css={styles.childrenContainer}>{children}</div>}
        </div>
      </Tooltip>
    </div>
  );
};

type AchievementTypes = typeof ACHIEVEMENT_TYPES & typeof ACHIEVEMENT_FALLBACK_TYPES;

type CompoundedComponent = MemoExoticComponent<typeof AchievementBase> & {
  SIZES: typeof STICKER_SIZES;
  TYPES: AchievementTypes;
  TIERS: typeof ACHIEVEMENT_TIERS;
};

const Achievement = memo(AchievementBase) as CompoundedComponent;

Achievement.SIZES = STICKER_SIZES;
Achievement.TYPES = {
  ...ACHIEVEMENT_TYPES,
  ...ACHIEVEMENT_FALLBACK_TYPES,
};
Achievement.TIERS = ACHIEVEMENT_TIERS;

export { Achievement };
